import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CustomModal from '../../common/CustomModal';
import CustomButton from '../../../components/CustomButton';
import LabelWithHelp from '../../../components/LabelWithHelp';
import { useCreateCampaignMutation } from '../../../api/voucher/voucherApiSlice';
import { CreateCampaignResult } from '../../../api/voucher/voucherApiTypes';
import { parseMemberEmails } from '../../../utils/memberCsv';
import CodeTermsFields, {
    ALL_OBJECTS_SCOPE,
    codeTermsValid,
    endOfDayIso,
    fieldClass,
    localToday,
} from './CodeTermsFields';
import { defaultCodeScope } from './codeScope';

// Promo-code campaign create (#2249): one code per e-mail address.
//
// The uploaded file is read here, in the browser, and never sent: only the
// addresses go to the backend (CEO decision 3 — no stored CSV). The same parser
// as the organisation members upload reads either a real CSV with an e-mail
// column or a pasted list.
//
// Every entry the parser found — valid and invalid — is sent, so the backend's
// own check produces the one "skipped" report the tenant sees (ADR-0020);
// duplicates are dropped here and only counted.

/** Mirrors `MAX_CAMPAIGN_RECIPIENTS` in `new-backend/admin/src/lib/voucher/campaignRecipients.ts`. */
const MAX_CAMPAIGN_RECIPIENTS = 2000;

/** Mirrors `readCodePrefix` in the same backend module: up to 16 letters or digits. */
const PREFIX_RE = /^[A-Za-z0-9]{0,16}$/;

interface Props {
    open: boolean;
    onClose: () => void;
    /** Called with the backend's result — the created codes and the skipped entries. */
    onCreated: (result: CreateCampaignResult) => void;
}

const serverMessage = (err: unknown): string | null =>
    typeof err === 'object' &&
    err !== null &&
    'data' in err &&
    typeof (err as { data?: { message?: string } }).data?.message === 'string'
        ? (err as { data: { message: string } }).data.message
        : null;

const CreateCampaignModal = ({ open, onClose, onCreated }: Props) => {
    const { t } = useTranslation();
    const [name, setName] = useState('');
    const [recipientsText, setRecipientsText] = useState('');
    const [codePrefix, setCodePrefix] = useState('');
    // #3052: campaign codes are promo codes — every station by default.
    const [groupId, setGroupId] = useState(defaultCodeScope());
    const [isPercentage, setIsPercentage] = useState(true);
    const [value, setValue] = useState('');
    const [validUntil, setValidUntil] = useState('');
    const [formError, setFormError] = useState<string | null>(null);
    const [createCampaign, { isLoading }] = useCreateCampaignMutation();

    const today = useMemo(localToday, []);
    const parsed = useMemo(() => parseMemberEmails(recipientsText), [recipientsText]);
    const submittedCount = parsed.valid.length + parsed.invalid.length;
    const tooMany = submittedCount > MAX_CAMPAIGN_RECIPIENTS;

    const isValid =
        name.trim().length > 0 &&
        parsed.valid.length > 0 &&
        !tooMany &&
        PREFIX_RE.test(codePrefix.trim()) &&
        codeTermsValid({ groupId, isPercentage, value, validUntil }, today);

    const close = () => {
        setName('');
        setRecipientsText('');
        setCodePrefix('');
        setGroupId(defaultCodeScope());
        setIsPercentage(true);
        setValue('');
        setValidUntil('');
        setFormError(null);
        onClose();
    };

    const handleFile = async (input: HTMLInputElement) => {
        const file = input.files?.[0];
        if (!file) return;
        setRecipientsText(await file.text());
        // Lets the same file be picked again after it was corrected.
        input.value = '';
    };

    const handleSubmit = async () => {
        setFormError(null);
        if (!isValid) {
            setFormError(t('vouchers_codes.campaign.validation'));
            return;
        }
        try {
            const result = await createCampaign({
                name: name.trim(),
                recipients: [...parsed.valid, ...parsed.invalid],
                ...(codePrefix.trim() ? { codePrefix: codePrefix.trim() } : {}),
                // Omitted for an all-objects campaign, exactly as for one code.
                ...(groupId === ALL_OBJECTS_SCOPE ? {} : { groupId }),
                discountValue: Number(value),
                discountIsPercentage: isPercentage,
                validUntilDate: endOfDayIso(validUntil),
            }).unwrap();
            onCreated(result);
            close();
        } catch (err) {
            setFormError(serverMessage(err) ?? t('vouchers_codes.campaign.error'));
        }
    };

    return (
        <CustomModal
            open={open}
            title={t('vouchers_codes.campaign.title')}
            onClose={close}
            closeButton={
                <CustomButton title={t('common.cancel')} isFilled={false} onClick={close} />
            }
            saveButton={
                <CustomButton
                    title={t('common.save')}
                    loading={isLoading}
                    disabled={!isValid || isLoading}
                    onClick={handleSubmit}
                />
            }
        >
            <div className={'flex flex-col gap-4'}>
                <div>
                    <LabelWithHelp
                        text={t('vouchers_codes.campaign.name_label') + ' *'}
                        help={t('vouchers_codes.campaign.name_help')}
                    />
                    <input
                        className={fieldClass}
                        value={name}
                        maxLength={255}
                        onChange={(e) => setName(e.target.value)}
                    />
                </div>

                <div>
                    <LabelWithHelp
                        text={t('vouchers_codes.campaign.recipients_label') + ' *'}
                        help={t('vouchers_codes.campaign.recipients_help')}
                    />
                    <textarea
                        className={fieldClass}
                        rows={5}
                        value={recipientsText}
                        placeholder={t('vouchers_codes.campaign.recipients_placeholder')}
                        onChange={(e) => setRecipientsText(e.target.value)}
                    />
                    <div className={'mt-2 flex items-center justify-between gap-3'}>
                        <p className={'text-xs text-foreground/50'}>
                            {recipientsText.trim().length > 0 &&
                                t('vouchers_codes.campaign.recipients_summary', {
                                    valid: parsed.valid.length,
                                    invalid: parsed.invalid.length,
                                    duplicates: parsed.duplicateCount,
                                })}
                        </p>
                        <label
                            className={
                                'shrink-0 cursor-pointer rounded-md border border-foreground/15 px-3 py-2 ' +
                                'text-sm font-medium text-foreground/70 hover:text-foreground'
                            }
                        >
                            {t('vouchers_codes.campaign.upload_csv')}
                            <input
                                type={'file'}
                                accept={'.csv,.txt,text/csv,text/plain'}
                                className={'sr-only'}
                                onChange={(e) => handleFile(e.target)}
                            />
                        </label>
                    </div>
                    {tooMany && (
                        <p className={'mt-1 text-xs text-foreground'}>
                            {t('vouchers_codes.campaign.recipients_too_many', {
                                max: MAX_CAMPAIGN_RECIPIENTS,
                            })}
                        </p>
                    )}
                </div>

                <div>
                    <LabelWithHelp
                        text={t('vouchers_codes.campaign.prefix_label')}
                        help={t('vouchers_codes.campaign.prefix_help')}
                    />
                    <input
                        className={fieldClass}
                        value={codePrefix}
                        maxLength={16}
                        placeholder={t('vouchers_codes.campaign.prefix_placeholder')}
                        onChange={(e) => setCodePrefix(e.target.value)}
                    />
                </div>

                <CodeTermsFields
                    open={open}
                    today={today}
                    groupId={groupId}
                    isPercentage={isPercentage}
                    value={value}
                    validUntil={validUntil}
                    onGroupIdChange={setGroupId}
                    onIsPercentageChange={setIsPercentage}
                    onValueChange={setValue}
                    onValidUntilChange={setValidUntil}
                />

                <p className={'text-xs text-foreground/50'}>
                    {t('vouchers_codes.campaign.once_hint')}
                </p>

                {formError && <p className={'text-sm text-red-500'}>{formError}</p>}
            </div>
        </CustomModal>
    );
};

export default CreateCampaignModal;
